
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Review } from "@/types/review";
import { Star, Bed, Bath, User, Calendar, ChevronLeft, ChevronRight, Globe, Check, CircleParking } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { translateParkingInfo } from "@/utils/translations";
import { getDisplayLandlordName } from "@/utils/addressUtils";

interface SingleReviewCardProps {
  review: Review;
  onClick?: () => void;
}

const DEFAULT_IMAGE = "https://cwuqmrrfvwddicutkrcc.supabase.co/storage/v1/object/public/review_images//logo%20(1).png";

const getRatingColor = (rating: number): string => {
  if (rating <= 2.5) return 'text-rating-low';
  if (rating <= 3.5) return 'text-rating-mid';
  return 'text-rating-high';
};

const getRatingBgColor = (rating: number): string => {
  if (rating <= 2.5) return 'bg-rating-low/10';
  if (rating <= 3.5) return 'bg-rating-mid/10';
  return 'bg-rating-high/10';
};

// Currency mapping based on country
const getCurrencyInfo = (country?: string) => {
  switch (country) {
    case 'TW':
      return { symbol: 'NT$', code: 'TWD' };
    case 'CA':
      return { symbol: 'CA$', code: 'CAD' };
    case 'US':
      return { symbol: 'US$', code: 'USD' };
    case 'AU':
      return { symbol: 'AU$', code: 'AUD' };
    case 'UK':
      return { symbol: '£', code: 'GBP' };
    default:
      return { symbol: 'CA$', code: 'CAD' }; // Default to CAD
  }
};

const SingleReviewCard: React.FC<SingleReviewCardProps> = ({
  review,
  onClick
}) => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const [userProfile, setUserProfile] = useState<{
    nickname: string;
    country?: string;
  } | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const hasImages = review.image_urls && review.image_urls.length > 0;
  const currencyInfo = getCurrencyInfo(review.country);

  // Determine display name (community name or landlord name with privacy settings, otherwise address)
  const displayLandlordName = getDisplayLandlordName(review.landlord_name, review.show_landlord_name, language);
  const displayAddress = review.display_address || review.address_detail;
  const displayName = review.community_name || (review.show_landlord_name !== false ? review.landlord_name : null) || displayAddress;
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      const {
        data,
        error
      } = await supabase.from('profiles').select('nickname, country').eq('id', review.user_id).single();
      if (!error && data) {
        setUserProfile({
          nickname: data.nickname,
          country: data.country
        });
      }
    };
    fetchUserProfile();
  }, [review.user_id]);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  const formatCurrency = (value: string | number) => {
    return Number(value).toLocaleString('en-CA');
  };

  // Format rental period years
  const getRentalYears = () => {
    if (review.rental_year_start) {
      // Only show the start year if no end year is provided
      if (!review.rental_year_end) {
        return `${review.rental_year_start}`;
      }
      return `${review.rental_year_start} - ${review.rental_year_end}`;
    }
    return null;
  };

  // Get the appropriate comment text based on translations and current language
  const getCommentText = () => {
    // If comment_translations exists and has a translation for the current language
    if (review.comment_translations && review.comment_translations[language]) {
      return review.comment_translations[language];
    }

    // Try individual fields if comment_translations doesn't have what we need
    if (language === 'zh-TW' && review.comment_zh_tw) {
      return review.comment_zh_tw;
    } else if (language === 'en' && review.comment_en) {
      return review.comment_en;
    }

    // Fall back to original comment if no translation is available
    return review.comment || '';
  };
  
  const nextImage = () => {
    if (review.image_urls && review.image_urls.length > 0) {
      setCurrentImageIndex(prev => prev === review.image_urls!.length - 1 ? 0 : prev + 1);
    }
  };
  
  const prevImage = () => {
    if (review.image_urls && review.image_urls.length > 0) {
      setCurrentImageIndex(prev => prev === 0 ? review.image_urls!.length - 1 : prev - 1);
    }
  };

  return <div>
    <Card className={`overflow-hidden ${getRatingBgColor(review.rating)} max-w-3xl mx-auto`}>
      {/* Image Gallery - always show, use default if no images */}
      <div className="relative w-full max-h-64 overflow-hidden">
          <div className="h-48 md:h-64 bg-gray-100">
            <img 
              src={hasImages ? review.image_urls[currentImageIndex] : DEFAULT_IMAGE} 
              alt={`Property image ${currentImageIndex + 1}`} 
              className="w-full h-full object-contain bg-white p-1" 
              onError={e => {
                (e.target as HTMLImageElement).src = DEFAULT_IMAGE;
              }} 
            />
          </div>
          
          {hasImages && review.image_urls && review.image_urls.length > 1 && <>
              <Button variant="outline" size="icon" className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 rounded-full hover:bg-white h-8 w-8" onClick={prevImage}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 rounded-full hover:bg-white h-8 w-8" onClick={nextImage}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                {review.image_urls.map((_, idx) => <button key={idx} className={`h-2 w-2 rounded-full ${idx === currentImageIndex ? 'bg-primary' : 'bg-gray-300'}`} onClick={() => setCurrentImageIndex(idx)} />)}
              </div>
            </>}
        </div>
      
      <CardContent className="p-3 md:p-4">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <div className={`flex items-center gap-1 ${getRatingColor(review.rating)}`}>
                <Star className="h-4 w-4 fill-current" />
                <span className="text-base font-semibold">{review.rating}</span>
              </div>
              
              {userProfile && <div className="flex items-center gap-1 text-xs">
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{userProfile.nickname}</span>
                  </div>
                  
                  {userProfile.country && <div className="flex items-center gap-1 ml-2">
                      <Globe className="h-3 w-3 text-muted-foreground" />
                      <span>{userProfile.country}</span>
                    </div>}
                </div>}
              
              <div className="flex items-center gap-1 text-xs font-medium">
                <span className="px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">
                  {language === 'zh-TW' 
                    ? (review.rental_status === 'viewed' ? '已看房' : review.rental_status === 'renting' ? '已承租' : '已結束租約') 
                    : t(`rentalStatus.${review.rental_status}`)}
                </span>
              </div>
              
              {getRentalYears() && <div className="flex items-center gap-1 text-xs">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span>{getRentalYears()}</span>
                </div>}
            </div>
            
            <div className="flex flex-col items-end">
              <span className="text-xs text-muted-foreground">
                {language === 'zh-TW' ? '最新評價' : 'Latest Review'}: {formatDate(review.created_at)}
              </span>
              
            </div>
          </div>
          
          <div className={`${isMobile ? 'flex flex-col' : 'grid grid-cols-2'} gap-y-1.5 gap-x-2 text-xs`}>
            <div className="flex flex-col mb-1">
              <span className="text-muted-foreground mb-0.5">{language === 'zh-TW' ? '月租金' : t('form.rent')}</span>
              <span className="font-medium">{currencyInfo.symbol} {formatCurrency(review.rent)}</span>
            </div>
            
            <div className="flex flex-col mb-1">
              <span className="text-muted-foreground mb-0.5">{language === 'zh-TW' ? '押金' : t('form.deposit')}</span>
              <span className="font-medium truncate">
                {review.deposit_type === 'amount' 
                  ? `${currencyInfo.symbol} ${formatCurrency(review.deposit_value)}` 
                  : `${review.deposit_value} ${language === 'zh-TW' ? '個月' : t('reviews.months')}`}
              </span>
            </div>
          </div>
          
          {/* Room information with icons and numbers */}
          <div className="flex flex-wrap gap-3 text-xs">
            {review.bedrooms > 0 && <div className="flex items-center gap-1">
                <Bed className="h-3 w-3 text-muted-foreground" />
                <span>{review.bedrooms}</span>
              </div>}
            {review.bathrooms > 0 && <div className="flex items-center gap-1">
                <Bath className="h-3 w-3 text-muted-foreground" />
                <span>{review.bathrooms}</span>
              </div>}
          </div>
          
          {/* Included utilities as badges */}
          <div className="mt-1">
            <span className="text-xs text-muted-foreground block mb-1">{language === 'zh-TW' ? '包含費用' : 'Included Utilities'}</span>
            <div className="flex flex-wrap gap-1.5">
              {review.utilities_included && review.utilities_included.length > 0 ? review.utilities_included.map(utility => <Badge key={utility} variant="outline" className="rounded-full px-2 py-0.5 text-xs bg-gray-100">
                    {language === 'zh-TW' ? 
                      (utility === 'water' ? '水費' : 
                       utility === 'electricity' ? '電費' : 
                       utility === 'heating' ? '暖氣費' : 
                       utility === 'internet' ? '網路費' : 
                       utility === 'gas' ? '瓦斯費' : 
                       utility === 'cable' ? '第四台' : 
                       utility === 'property_management' ? '物業管理費' :
                       utility === 'other' ? '其他' :
                       utility === 'maintenance' ? '管理費' : utility)
                    : t(`utilities.${utility}`)}
                  </Badge>) : <span className="text-xs text-muted-foreground">{language === 'zh-TW' ? '無' : 'None'}</span>}
            </div>
          </div>
          
          {/* Detailed Feature Categories */}
          <div className="mt-3 pt-3 border-t">
            <h4 className="font-medium text-sm mb-2">{language === 'zh-TW' ? '房源特色' : 'Property Features'}</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pet-friendly details */}
              {(review.pet_allowed || review.no_pet_limit_count || review.no_pet_limit_type || review.pet_fee) && <div className="border rounded-md p-2">
                  <h5 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '寵物友善程度' : 'Pet Friendly Level'}</h5>
                  <div className="space-y-1">
                    {review.pet_allowed && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '可養寵物' : 'Pets Allowed'}</span>
                      </div>}
                    {review.no_pet_limit_count && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '不限寵物數量' : 'No Pet Count Limit'}</span>
                      </div>}
                    {review.no_pet_limit_type && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '不限寵物類型' : 'No Pet Type Limit'}</span>
                      </div>}
                    {review.pet_fee && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '需要寵物費用' : 'Pet Fee Required'}</span>
                      </div>}
                  </div>
                </div>}
              
              {/* Immigrant-friendly features */}
              {(review.accepts_overseas_proof_of_funds || review.accepts_overseas_employment_proof) && <div className="border rounded-md p-2">
                  <h5 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '新移民友善程度' : 'Immigrant Friendly Level'}</h5>
                  <div className="space-y-1">
                    {review.accepts_overseas_proof_of_funds && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '接受海外資金證明' : 'Accepts Overseas Proof of Funds'}</span>
                      </div>}
                    {review.accepts_overseas_employment_proof && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '接受海外工作證明' : 'Accepts Overseas Employment Proof'}</span>
                      </div>}
                  </div>
                </div>}
              
              {/* Application ease details - show if any easy application features are available */}
              {(review.no_proof_required || 
                !review.requires_employment_proof || 
                !review.requires_credit_check || 
                !review.has_income_requirement || 
                !review.requires_financial_proof) && <div className="border rounded-md p-2">
                  <h5 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '容易申請程度' : 'Easy Application Level'}</h5>
                  <div className="space-y-1">
                    {review.no_proof_required && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '無需提供任何證明' : 'No Proof Required'}</span>
                      </div>}
                    {!review.requires_employment_proof && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '無需工作證明' : 'No Employment Proof Required'}</span>
                      </div>}
                    {!review.requires_credit_check && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '無需信用檢查' : 'No Credit Check Required'}</span>
                      </div>}
                    {!review.has_income_requirement && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '無收入要求' : 'No Income Requirement'}</span>
                      </div>}
                    {!review.requires_financial_proof && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '無需財力證明' : 'No Financial Proof Required'}</span>
                      </div>}
                  </div>
                </div>}
              
              {/* Contract flexibility details */}
              {(review.allows_daily_rental || review.allows_monthly_rental || review.allows_semiannual_rental || review.allows_contract_termination || review.allows_contract_transfer) && <div className="border rounded-md p-2">
                  <h5 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '合約彈性程度' : 'Contract Flexibility'}</h5>
                  <div className="space-y-1">
                    {review.allows_daily_rental && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '允許日租' : 'Daily Rental Allowed'}</span>
                      </div>}
                    {review.allows_monthly_rental && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '允許月租' : 'Monthly Rental Allowed'}</span>
                      </div>}
                    {review.allows_semiannual_rental && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '允許半年租約' : 'Semi-annual Rental Allowed'}</span>
                      </div>}
                    {review.allows_contract_termination && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '允許提前解約' : 'Early Termination Allowed'}</span>
                      </div>}
                    {review.allows_contract_transfer && <div className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{language === 'zh-TW' ? '允許轉租' : 'Contract Transfer Allowed'}</span>
                      </div>}
                  </div>
                </div>}

              {/* Parking information block - with proper translation */}
              {review.parking_information && review.parking_information.length > 0 && (
                <div className="border rounded-md p-2">
                  <h5 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '停車資訊' : 'Parking Information'}</h5>
                  <div className="space-y-1">
                    {review.parking_information.map((info, index) => (
                      <div key={index} className="flex items-center text-xs">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        <span>{translateParkingInfo(info, language)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Tenant experience & comments */}
          {review.comment && <div className="pt-2 border-t mt-2">
              <h4 className="font-medium text-xs mb-1">{language === 'zh-TW' ? '綜合評價' : 'Overall Review'}</h4>
              <div className="text-xs max-w-full">
                <p>{getCommentText()}</p>
              </div>
            </div>}
        </div>
      </CardContent>
    </Card>
  </div>;
};

export default SingleReviewCard;
